import { useAuth } from '../context/AuthContext';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contractConfigApi, type PingDebugBank } from '../services/api';

/** Deliberately separate from the platform: this page represents the bank. */
export function DebugBank() {
  const { paymentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [bank, setBank] = useState<PingDebugBank | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  useEffect(() => {
    let active = true;
    setBank(null); setError(false);
    if (!paymentId) { setError(true); return; }
    void contractConfigApi.getPingDebugBank(paymentId).then(value => {
      if (active) setBank(value);
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [paymentId]);
  useEffect(() => {
    const previous = document.title;
    document.title = t('debugBank.title');
    return () => { document.title = previous; };
  }, [t]);

  const pay = async () => {
    if (!bank?.canPay || inFlight.current || !paymentId) return;
    inFlight.current = true; setBusy(true);
    // Keep payment disabled after any POST result, including a lost response.
    setBank(value => value && { ...value, canPay: false });
    try {
      const result = await contractConfigApi.payPingDebugBank(paymentId);
      setBank(value => value && { ...value, ...result, canPay: false });
      if (result.transferStatus === 'accepted') {
        // Mimic acceptance is not funding confirmation. The normal reconciliation
        // updates the project's persisted status, or leaves it awaiting payment.
        await contractConfigApi.syncPingDeposit(paymentId).catch(() => undefined);
        navigate(returnUrl, { replace: true });
      }
    } catch {
      setBank(value => value && { ...value, transferStatus: 'uncertain', canPay: false });
    } finally { setBusy(false); inFlight.current = false; }
  };
  const money = (ore: number | null) => ore === null ? t('pingDeposit.notConfirmed') :
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: bank?.currency ?? 'SEK' }).format(ore / 100);
  const projectBase = user?.personaType === 'person' ? '/person/projects' : user?.isAdmin ? '/admin/projects' : '/projects';
  const returnUrl = bank ? `${projectBase}/${bank.projectId}?${new URLSearchParams({ tab: 'contract', contractId: bank.contractId })}` : '/home';


  return <main className="min-vh-100 bg-light p-3 p-sm-4">
    <div className="container py-3 py-sm-5">
      <div className="row justify-content-center"><div className="col-12 col-md-9 col-lg-7">
        <section className="card border-warning shadow-sm text-break" aria-labelledby="debug-bank-title">
          <header className="card-header bg-warning text-dark p-3">
            <span className="badge bg-dark mb-2">{t('debugBank.badge')}</span>
            <h1 id="debug-bank-title" className="h3 mb-0"><i className="bi bi-bank me-2" aria-hidden="true" />{t('debugBank.title')}</h1>
          </header>
          <div className="card-body p-3 p-sm-4">
            <p className="lead">{t('debugBank.description')}</p>
            <p>{t('debugBank.explanation')}</p>
            {error && <p className="alert alert-danger" role="alert">{t('debugBank.loadError')}</p>}
            {!bank && !error && <p role="status">{t('debugBank.loading')}</p>}
            {bank && <>
              <dl className="row g-2 my-4">
                <dt className="col-12 col-sm-5">{t('pingDeposit.reference')}</dt><dd className="col-12 col-sm-7">{bank.reference}</dd>
                <dt className="col-12 col-sm-5">{t('pingDeposit.bank.iban')}</dt><dd className="col-12 col-sm-7">{bank.iban}</dd>
                <dt className="col-12 col-sm-5">{t('debugBank.invoiceAmount')}</dt><dd className="col-12 col-sm-7">{money(bank.invoiceAmountOre)}</dd>
                <dt className="col-12 col-sm-5">{t('pingDeposit.received')}</dt><dd className="col-12 col-sm-7">{money(bank.receivedOre)}</dd>
                <dt className="col-12 col-sm-5">{t('debugBank.amount')}</dt><dd className="col-12 col-sm-7 fw-bold">{money(bank.amountOre)}</dd>
              </dl>
              {bank.transferStatus !== 'not_sent' && <p className={`alert ${bank.transferStatus === 'accepted' ? 'alert-success' : 'alert-warning'}`} role="status">{t(`debugBank.${bank.transferStatus}`)}</p>}
              {bank.transferStatus === 'not_sent' && !bank.canPay && <p role="status" className="alert alert-info">{t('debugBank.notPayable')}</p>}
              <button type="button" className="btn btn-primary btn-lg w-100 mb-3" disabled={busy || !bank.canPay} onClick={() => void pay()}>{t(busy ? 'debugBank.paying' : 'debugBank.pay')}</button>
            </>}
            <Link className="btn btn-outline-secondary btn-lg w-100" to={returnUrl}>{t(bank ? 'paymentCheckout.backToProject' : 'debugBank.home')}</Link>
          </div>
        </section>
      </div></div>
    </div>
  </main>;
}
